import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

import { UserService } from '@ppg/auth';
import { CurrentSiteState } from '@ppg/configuration';
import { ClientSiteSettingsKeys } from '@ppg/core/enums';
import { LanguagesService } from '@ppg/core/language';
import { FeatureFlagService } from '@ppg/shared/feature-flag';

import { DeliveryType } from '../enums/delivery-type.enum';
import { InventoryProductColorType } from '../enums/inventory-product-color-type.enum';
import { InventoryProductType } from '../enums/inventory-product-type.enum';
import { B2BInventoryStatusQuery } from '../queries/b2b-inventory-status.query';
import {
  B2CInventoryStatusQuery,
  B2cInventoryStatusQueryVariables,
  getB2CInventoryStatusQuery,
  GetInventoryStatusQueryInputParam,
} from '../queries/b2c-inventory-status.query';
import { getContextClientNameIfAnonymousTransactable } from '../utils/get-context-client-name.util';

export interface GetInventoryParams {
  deliveryType: DeliveryType;
  storeSapId?: string;
  productType: InventoryProductType;
  colorType?: InventoryProductColorType;
  quantity: number;
  sKU: string;
  exportCatalogSKU: string;
}

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  readonly #b2bInventoryStatusQuery = inject(B2BInventoryStatusQuery);
  readonly #b2cInventoryStatusQuery = inject(B2CInventoryStatusQuery);
  readonly #languageService = inject(LanguagesService);
  readonly #userService = inject(UserService);
  readonly #featureFlagService = inject(FeatureFlagService);
  readonly #currentSiteState = inject(CurrentSiteState);

  readonly isProductDetailsAnonymous = this.#currentSiteState.getSiteFlag(
    ClientSiteSettingsKeys.AnonymousJourneyProductDetailsAnonymousEnabled,
  );

  readonly #globalSettings = toSignal(
    this.#featureFlagService.getGlobalSettingsAsAnonymous(this.#languageService.language()),
  );

  readonly #contextClientName = computed(() =>
    getContextClientNameIfAnonymousTransactable(this.#globalSettings(), this.isProductDetailsAnonymous),
  );

  getInventory(params: GetInventoryParams) {
    const isAuthenticated = Boolean(this.#userService.user());
    const isB2BUser = this.#userService.isB2BUser();

    const inventoryParams = this.#buildInventoryBaseVariables(params);

    return isAuthenticated && isB2BUser
      ? this.#b2bInventoryStatusQuery
          .fetch({ inventory: { ...inventoryParams, storeSapId: params.storeSapId! } })
          .pipe(map((response) => response.data.inventory))
      : this.#b2cInventoryStatusQuery
          .fetch({ inventory: inventoryParams }, this.#contextClientName())
          .pipe(map((response) => response.data.inventoryStatus));
  }

  prepareB2CInventoryCombinedQuery(combinedParams: Record<string, GetInventoryParams>): {
    queryBody: string;
    queryArgs: string;
    variables: B2cInventoryStatusQueryVariables<string>;
  } {
    const { queryBody, queryArgs, variables } = Object.entries(combinedParams).reduce(
      (acc, [key, params]) => {
        acc.variables[key] = this.#buildInventoryBaseVariables(params);

        acc.queryArgs.push(`$${key}: ${GetInventoryStatusQueryInputParam}`);
        acc.queryBody += getB2CInventoryStatusQuery(key);

        return acc;
      },
      {
        queryArgs: [] as string[],
        queryBody: '',
        variables: {} as B2cInventoryStatusQueryVariables<string>,
      },
    );

    return {
      queryBody,
      queryArgs: queryArgs.join(', '),
      variables,
    };
  }

  #buildInventoryBaseVariables({
    deliveryType,
    productType,
    colorType,
    quantity,
    sKU,
    exportCatalogSKU,
  }: GetInventoryParams): B2cInventoryStatusQueryVariables<string>[string] {
    return {
      ...this.#getRequestedDateTime(),
      languageCode: this.#languageService.language(),
      deliveryType: deliveryType,
      items: [
        {
          itemNumber: 1,
          quantity,
          sKU,
          exportCatalogSKU,
          colorType,
          productType,
        },
      ],
    };
  }

  #getRequestedDateTime() {
    const dateTime = new Date();
    const requestedFromDate = dateTime.toUTCString();
    const requestedToDate = new Date(dateTime.setHours(dateTime.getHours() + 4)).toUTCString();
    return { requestedFromDate, requestedToDate };
  }
}
